import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, Linkedin, Youtube, ArrowRight } from 'lucide-react';
import { SITE } from '../data/mock';
import Logo from './Logo';
import NewsletterSignup from './NewsletterSignup';

export default function Footer() {
  return (
    <footer className="relative mt-0 bg-[#0d0a1f] text-slate-300 overflow-hidden">
      <div className="absolute inset-0 dot-pattern opacity-40 pointer-events-none" />
      <div className="absolute -top-40 -right-40 w-96 h-96 bg-violet-500/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-violet-700/10 rounded-full blur-3xl pointer-events-none" />

      <div className="relative container-x py-16 grid lg:grid-cols-4 md:grid-cols-2 gap-10">
        <div>
          <Link to="/" className="inline-flex items-center mb-5">
            <Logo variant="white" className="h-10 w-auto" />
          </Link>
          <p className="text-sm text-slate-400 leading-relaxed mb-5 max-w-xs">
            Cloud-based VoIP telephony built for modern teams. HD calls, smart routing & 250+ CRM integrations.
          </p>
          <div className="flex flex-col gap-2 text-sm">
            <a className="flex items-center gap-2 hover:text-violet-400 transition-colors" href={`tel:${SITE.phoneTel}`}>
              <Phone size={14} className="text-violet-400" /> {SITE.phone}
            </a>
            <a className="flex items-center gap-2 hover:text-violet-400 transition-colors" href={`mailto:${SITE.email}`}>
              <Mail size={14} className="text-violet-400" /> {SITE.email}
            </a>
            <p className="flex items-start gap-2"><MapPin size={14} className="text-violet-400 mt-1" /> {SITE.address}</p>
            <p className="flex items-center gap-2"><Clock size={14} className="text-violet-400" /> {SITE.hours}</p>
          </div>
          <div className="flex items-center gap-3 mt-5">
            <a href={SITE.linkedin} target="_blank" rel="noreferrer" aria-label="LinkedIn" className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:bg-violet-500/20 hover:border-violet-400/40 transition-colors">
              <Linkedin size={16} />
            </a>
            <a href={SITE.youtube} target="_blank" rel="noreferrer" aria-label="YouTube" className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:bg-violet-500/20 hover:border-violet-400/40 transition-colors">
              <Youtube size={16} />
            </a>
          </div>
        </div>

        <div>
          <h4 className="text-white font-semibold mb-4">Services</h4>
          <ul className="space-y-2 text-sm">
            {/* Order mirrors the Services mega-menu: phone systems first,
                then numbers, then contact-centre, then add-on features. */}
            <li><Link to="/business-voip" className="hover:text-violet-400 transition-colors">Cloud Hosted PBX</Link></li>
            <li><Link to="/microsoft-teams" className="hover:text-violet-400 transition-colors">Microsoft Teams Phone</Link></li>
            <li><Link to="/sip-trunk" className="hover:text-violet-400 transition-colors">SIP Trunk</Link></li>
            <li><Link to="/virtual-numbers" className="hover:text-violet-400 transition-colors">Phone Numbers / DID</Link></li>
            <li><Link to="/crm-integrations" className="hover:text-violet-400 transition-colors">CRM Integrations</Link></li>
            <li><Link to="/inbound-contact-centre" className="hover:text-violet-400 transition-colors">Inbound Contact Centre</Link></li>
            <li><Link to="/outbound-contact-centre" className="hover:text-violet-400 transition-colors">Outbound Contact Centre</Link></li>
            <li><Link to="/web-phone" className="hover:text-violet-400 transition-colors">Web Phone & Softphone</Link></li>
            <li><Link to="/video-conferencing" className="hover:text-violet-400 transition-colors">Video Conferencing</Link></li>
            <li><Link to="/unified-communications" className="hover:text-violet-400 transition-colors">Unified Communications</Link></li>
            <li><Link to="/phone-features" className="hover:text-violet-400 transition-colors">Phone System Features</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="text-white font-semibold mb-4">Partners</h4>
          <ul className="space-y-2 text-sm mb-6">
            <li><Link to="/white-label" className="hover:text-violet-400 transition-colors">White Label VoIP</Link></li>
            <li><Link to="/wholesale-voip" className="hover:text-violet-400 transition-colors">Wholesale VoIP</Link></li>
            <li><Link to="/managed-infrastructure" className="hover:text-violet-400 transition-colors">Managed Infrastructure</Link></li>
          </ul>
          <h4 className="text-white font-semibold mb-4">Company</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/about" className="hover:text-violet-400 transition-colors">About Us</Link></li>
            <li><Link to="/awards" className="hover:text-violet-400 transition-colors">Awards & Recognition</Link></li>
            <li><Link to="/reviews" className="hover:text-violet-400 transition-colors">Customer Reviews</Link></li>
            <li><Link to="/blog" className="hover:text-violet-400 transition-colors">Blog & Resources</Link></li>
            <li><Link to="/pricing" className="hover:text-violet-400 transition-colors">Pricing</Link></li>
            <li><Link to="/status" className="hover:text-violet-400 transition-colors">Service status</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="text-white font-semibold mb-4">Get in touch</h4>
          <p className="text-sm text-slate-400 mb-4">
            Tell us what you need. We’ll prepare a tailored proposal for your business.
          </p>
          <Link to="/contact" className="btn-primary text-sm w-full justify-center">
            Contact Sales <ArrowRight size={16} />
          </Link>
          <div className="mt-6 grid grid-cols-3 gap-2 text-center text-xs">
            <div className="bg-white/5 rounded-lg py-3 border border-white/5">
              <p className="font-bold text-white">Next day</p>
              <p className="text-slate-400">setup</p>
            </div>
            <div className="bg-white/5 rounded-lg py-3 border border-white/5">
              <p className="font-bold text-white">14 days</p>
              <p className="text-slate-400">free trial</p>
            </div>
            <div className="bg-white/5 rounded-lg py-3 border border-white/5">
              <p className="font-bold text-white">Pro</p>
              <p className="text-slate-400">support</p>
            </div>
          </div>

          {/* Newsletter capture sits below the contact CTA so it doesn't
              compete with the primary footer action, but stays inside the
              "Get in touch" column for narrative cohesion. */}
          <div className="mt-6 pt-6 border-t border-white/10">
            <NewsletterSignup variant="footer" source="footer" />
          </div>
        </div>
      </div>

      <div className="relative border-t border-white/10">
        <div className="container-x py-5 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-slate-500">
          <p>© {new Date().getFullYear()} VoIPinger. All rights reserved.</p>
          <div className="flex flex-wrap items-center gap-4">
            <Link to="/terms-conditions" className="hover:text-violet-400">Terms &amp; Conditions</Link>
            <Link to="/privacy-policy" className="hover:text-violet-400">Privacy Policy</Link>
            <Link to="/cookies-policy" className="hover:text-violet-400">Cookies</Link>
            <Link to="/acceptable-use-policy" className="hover:text-violet-400">Acceptable Use</Link>
            <a href="/sitemap.xml" className="hover:text-violet-400">Sitemap</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
